// ── Elo Rating Engine v2 — "Safari inaanzia sifuri" ─────────────────────
// EVERY player starts at 0. High ratings are meant to be RARE and earned:
// reaching 2400+ (Simba) takes hundreds of games and real tournament
// success, so the ladder never loses its meaning.
//
// Points depend on WHO you played:
//  • BOT games    — tiny movement (max a few points). Practice, not farming.
//  • HUMAN games  — the real ladder: big gains AND big losses. This is the
//    heartbeat of the app — play people, move fast.
//  • TOURNAMENTS  — the biggest K of all. Tournaments are where serious
//    rating is won (and lost).

export const DEFAULT_RATING = 0;
const RATING_FLOOR = 0; // the journey starts (and can restart) at zero

/** K factor by game context and current rating. */
function kFor(rating, context) {
  if (context === 'bot') return 6;              // ±3 vs equal opponent
  if (context === 'tournament') {
    if (rating < 700) return 56;
    if (rating < 1800) return 44;
    return 32;
  }
  // human (casual/quick/friend) — the everyday ladder
  if (rating < 700) return 36;
  if (rating < 1800) return 28;
  return 18;
}

/**
 * Calculate a player's new rating after a game.
 * @param {number} playerRating
 * @param {number} opponentRating
 * @param {number} score - 1 = win, 0.5 = draw, 0 = loss
 * @param {'bot'|'human'|'tournament'} context
 */
export function calculateNewRating(playerRating, opponentRating, score, context = 'human') {
  const expected = 1 / (1 + Math.pow(10, (opponentRating - playerRating) / 400));
  const k = kFor(playerRating, context);
  const newRating = Math.round(playerRating + k * (score - expected));
  return Math.max(RATING_FLOOR, newRating);
}

/**
 * New ratings for both players after a game.
 * @param {'white'|'black'|'draw'} winner
 * @param {'bot'|'human'|'tournament'} context
 */
export function calculateGameRatings(whiteRating, blackRating, winner, context = 'human') {
  let whiteScore, blackScore;
  if (winner === 'white') { whiteScore = 1; blackScore = 0; }
  else if (winner === 'black') { whiteScore = 0; blackScore = 1; }
  else { whiteScore = 0.5; blackScore = 0.5; }

  return {
    newWhiteRating: calculateNewRating(whiteRating, blackRating, whiteScore, context),
    newBlackRating: calculateNewRating(blackRating, whiteRating, blackScore, context),
  };
}

// ── ACTIVITY BONUS — thawabu ya kukutana ana kwa ana ───────────────────
// Ili kuhamasisha watu KUKUTANA (kwenye mashindano na chess club), kila
// mchezo wa aina hizo unaongeza pointi za ziada JUU ya matokeo ya kawaida
// ya Elo — hata ukishindwa, kuhudhuria kunakupa faida ndogo. Hii inafanya
// mtu anayeshiriki sana apande haraka kuliko anayecheza peke yake na bot.
//
//  • tournament : +4 kila mchezo (mkutano rasmi wenye ushindani mkubwa)
//  • club       : +3 kila mchezo (mkutano wa klabu / ana kwa ana)
//  • human/bot  : +0 (hakuna mkutano wa kimwili)
//
// Bonus ni ya kila mchezo mmoja; anayecheza mingi kwenye mikutano
// anajenga rating kwa kasi — lengo ni kuwavuta watu wakusanyike.
export const ACTIVITY_BONUS = { tournament: 4, club: 3, human: 0, bot: 0 };

export function activityBonus(context) {
  return ACTIVITY_BONUS[context] || 0;
}

// ── Swahili rating tiers — rescaled for the from-zero journey ───────────
export const CHESS_TIERS = [
  { min: 2400, swahili: 'Simba', color: '#F87171' },
  { min: 1800, swahili: 'Chui', color: '#FB923C' },
  { min: 1200, swahili: 'Tembo', color: '#60A5FA' },
  { min: 700, swahili: 'Twiga', color: '#C084FC' },
  { min: 300, swahili: 'Swala', color: '#2DD4BF' },
  { min: 0, swahili: 'Mchipukizi', color: '#9CA3AF' },
];

export function getTier(rating) {
  return CHESS_TIERS.find(t => rating >= t.min) || CHESS_TIERS[CHESS_TIERS.length - 1];
}

export function getLevelBadge(level) {
  switch (level) {
    case 'Advanced': return { color: '#0E9F6E', bg: '#E7F7EF' };
    case 'Intermediate': return { color: '#B45309', bg: '#FEF3E2' };
    default: return { color: '#1D4ED8', bg: '#E8F0FE' };
  }
}
